// TEMPORARY upload-method experiment (docs/measurement-research.md §16). Delete together with upload-ab.ts.
import { captureEnvironment, newRunId, type EnvironmentInfo } from './environment';
import {
  checkHealth,
  cloudflareColo,
  measureUpload,
  perSecondMbps,
  UPLOAD_AB_METHOD,
  waitForFreshMachineMs,
  waitUntilHealthy,
  windowMbps,
  type UploadMethod,
  type UploadTargetName,
  type UploadTargetResult,
} from './upload-ab';

const BASE: UploadMethod = {
  streams: UPLOAD_AB_METHOD.streams,
  streamStaggerMs: UPLOAD_AB_METHOD.streamStaggerMs,
  rampUpMs: UPLOAD_AB_METHOD.rampUpMs,
  windowMs: UPLOAD_AB_METHOD.windowMs,
  initialChunkBytes: UPLOAD_AB_METHOD.initialChunkBytes,
  maxChunkBytes: UPLOAD_AB_METHOD.maxChunkBytes,
  growIfFasterThanMs: UPLOAD_AB_METHOD.growIfFasterThanMs,
};

export type VariantName = 'A' | 'B' | 'C';

/** Each variant changes exactly one parameter of the current method (A). */
export const UPLOAD_METHOD_VARIANTS: Record<VariantName, UploadMethod> = {
  A: BASE,
  // Longer warm-up: tests whether the uplink is still ramping (radio grants, congestion window) during A's window.
  B: { ...BASE, rampUpMs: 4000 },
  // More parallel requests: tests per-flow limits. 6 is the browsers' HTTP/1.1 per-host connection limit.
  C: { ...BASE, streams: 6 },
};

/** Every variant appears once in every position across three consecutive runs, to cancel slow network drift. */
export const VARIANT_ROTATIONS: VariantName[][] = [
  ['A', 'B', 'C'],
  ['B', 'C', 'A'],
  ['C', 'A', 'B'],
];

/** A's measurement window, recomputed from B's timeline: same run, same conditions, only the window differs. */
export const A_WINDOW = { fromMs: BASE.rampUpMs, toMs: BASE.rampUpMs + BASE.windowMs };

const PAUSE_BETWEEN_VARIANTS_MS = 1500;

export interface VariantResult extends UploadTargetResult {
  variant: VariantName;
  perSecondMbps: number[];
  /** Throughput over A's 1–5 s window, computed from this variant's own timeline. */
  aWindowMbps: number;
}

export interface UploadMethodRun {
  schema: 'measurement-spike/v1';
  kind: 'upload-method';
  id: string;
  label: string;
  startedAt: string;
  environment: EnvironmentInfo;
  target: UploadTargetName;
  variants: Record<VariantName, UploadMethod>;
  order: VariantName[];
  cloudflareColo: string | null;
  receiverRegion: string | null;
  receiverBootId: string | null;
  fastComUploadMbps: number | null;
  results: VariantResult[];
  status: 'valid' | 'invalid';
  invalidReasons: string[];
  wallClockMs: number;
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export async function runUploadMethodExperiment(options: {
  label: string;
  target: UploadTargetName;
  receiverUrl: string;
  fastComUploadMbps: number | null;
  rotationIndex: number;
  onStatus?: (message: string) => void;
}): Promise<UploadMethodRun> {
  const status = options.onStatus ?? (() => {});
  const t0 = performance.now();
  const startedAt = new Date().toISOString();
  const order = VARIANT_ROTATIONS[options.rotationIndex % VARIANT_ROTATIONS.length] ?? VARIANT_ROTATIONS[0]!;
  const url = options.target === 'worker' ? '/up?method=1' : `${options.receiverUrl}/up`;
  const invalidReasons: string[] = [];
  const results: VariantResult[] = [];
  const colo = await cloudflareColo();

  let bootId: string | null = null;
  let region: string | null = null;
  if (options.target === 'receiver') {
    status('Waking the upload receiver…');
    let health = await waitUntilHealthy(options.receiverUrl);
    const wait = health.ok && health.uptimeMs !== null ? waitForFreshMachineMs(health.uptimeMs) : 0;
    if (wait > 0) {
      status(`Receiver is near the Fly trial's 5-minute limit; waiting ${Math.round(wait / 1000)} s for a fresh machine…`);
      await sleep(wait);
      health = await waitUntilHealthy(options.receiverUrl);
    }
    bootId = health.bootId;
    region = health.region;
    if (!health.ok || !bootId) invalidReasons.push(`receiver not healthy before the test: ${health.error ?? 'no boot ID'}`);
  }

  if (!invalidReasons.length) {
    for (const [i, variant] of order.entries()) {
      if (i > 0) await sleep(PAUSE_BETWEEN_VARIANTS_MS);
      status(`Variant ${variant} (${i + 1}/3)…`);
      const result = await measureUpload(options.target, url, bootId, UPLOAD_METHOD_VARIANTS[variant]);
      results.push({
        ...result,
        variant,
        perSecondMbps: perSecondMbps(result.timeline),
        aWindowMbps: windowMbps(result.timeline, A_WINDOW.fromMs, A_WINDOW.toMs),
      });
      for (const error of result.errors) invalidReasons.push(`${variant}: ${error}`);
    }
    if (options.target === 'receiver') {
      const after = await checkHealth(options.receiverUrl);
      if (!after.ok) invalidReasons.push(`receiver unhealthy after the test: ${after.error ?? 'unknown'}`);
      else if (after.bootId !== bootId) invalidReasons.push('receiver restarted during the run (boot ID changed)');
    }
  }

  return {
    schema: 'measurement-spike/v1',
    kind: 'upload-method',
    id: newRunId(),
    label: options.label,
    startedAt,
    environment: captureEnvironment(),
    target: options.target,
    variants: UPLOAD_METHOD_VARIANTS,
    order,
    cloudflareColo: colo,
    receiverRegion: region,
    receiverBootId: bootId,
    fastComUploadMbps: options.fastComUploadMbps,
    results,
    status: invalidReasons.length ? 'invalid' : 'valid',
    invalidReasons,
    wallClockMs: performance.now() - t0,
  };
}
